import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Skeleton,
  Typography,
  Chip,
  Pagination,
  Box,
  Badge,
  Tooltip,
  IconButton, // Add Pagination from MUI
} from "@mui/material";
import axios from "axios";
import { API_SERVICE_BACKEND } from "src/config";
import PersonIcon from "@mui/icons-material/Person";

import { Delete } from "@mui/icons-material";
import toast from "react-hot-toast";
import { downloadBlobFile } from "src/utils/getBlobUrl";

const ExportLeadTable = ({
  userEmail,
  selectedExportId,
  type,
  handleApplyFilter,
}) => {
  const [exportJobs, setExportJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchExportJobs = async (page = 1) => {
    try {
      setLoading(true);
      let payload = {
        owner: userEmail,
        page,
        limit: 10,
      };

      const response = await axios.get(`${API_SERVICE_BACKEND}/export-lead`, {
        params: payload,
      });
      setExportJobs(response.data?.exportlead);
      setTotalPages(response.data?.pagination?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching export jobs:", error);
    } finally {
      setLoading(false);
    }
  };
  const deletejob = async (id) => {
    try {
      setLoading(true);

      const response = await axios.delete(
        `${API_SERVICE_BACKEND}/export-lead/${id}`
      );
      toast.success(
        response?.data?.message || "Export lead job deleted successfully"
      );

      fetchExportJobs(currentPage);
    } catch (error) {
      console.error("Error fetching export jobs:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchExportJobs(currentPage);
  }, [userEmail, currentPage]);

  const getStatusDisplay = (status, error) => {
    let color;
    let label;
    let className;

    switch (status) {
      case "RUNNING":
        color = "primary";
        label = "Running";
        className = "glass-chip-primary";
        break;
      case "PAUSED":
        color = "warning";
        label = "Paused";
        className = "glass-chip-warning";
        break;
      case "FAILED":
        color = "error";
        label = "Failed";
        className = "glass-chip-error";
        break;
      case "COMPLETED":
        color = "success";
        label = "Completed";
        className = "glass-chip-success";
        break;
      case "NA":
        color = "default";
        label = "N/A";
        className = "glass-chip";
        break;
      default:
        color = "default";
        label = "Unknown";
        className = "glass-chip";
    }

    return (
      <>
        {status === "FAILED" && error && error[0]?.message.length > 0 ? (
          <Tooltip title={error[0]?.message} placement="top" arrow>
            <Chip label={label} color={color} className={className} />
          </Tooltip>
        ) : (
          <Chip label={label} color={color} className={className} />
        )}
      </>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const formatter = new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return formatter.format(date);
  };

  if (loading) {
    return (
      <Box>
        <TableContainer
          component={Paper}
          sx={{ borderRadius: 2, border: "1px solid #ddd" }}
          className="glass-wrapper"
        >
          <Table>
            <TableHead
              className="glass-table-header"
              sx={{
                "&.MuiTableHead-root .MuiTableCell-root": {
                  backgroundColor: "rgb(233, 233, 236)",
                  color: (theme) => theme.palette.text.primary,
                },
              }}
            >
              <TableRow>
                <TableCell>
                  <strong>File Name</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Created Date</strong>
                </TableCell>

                <TableCell align="center">
                  <strong>Status</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Download</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(5)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton width="100px" />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton width="80px" />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton width="80px" />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton width="100px" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          border: "1px solid #ddd",
          overflow: "hidden",
          position: "relative",
          height: "calc(100vh - 200px)",
        }}
        className="glass-wrapper-50"
      >
        <Box
          sx={{ height: "calc(100% - 50px)", overflow: "auto", mb: "60px" }}
          className="glass-scrollbar"
        >
          <Table stickyHeader>
            <TableHead
              className="glass-table-header"
              sx={{
                "&.MuiTableHead-root .MuiTableCell-root": {
                  backgroundColor: "rgb(233, 233, 236)",
                  color: (theme) => theme.palette.text.primary,
                },
              }}
            >
              <TableRow>
                <TableCell>
                  <strong>File Name</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Created Date</strong>
                </TableCell>

                <TableCell align="center">
                  <strong>Status</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Download</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Option</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {exportJobs?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="subtitle1" color="textSecondary">
                      No exported files available.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                exportJobs?.map((job) => (
                  <TableRow
                    key={job._id}
                    sx={{
                      backgroundColor:
                        job._id === selectedExportId
                          ? "rgba(255, 235, 59, 0.3)"
                          : "inherit",
                      transition: "background-color 0.3s ease",
                    }}
                  >
                    <TableCell sx={{ textWrap: "nowrap" }}>
                      <div style={{ display: "flex", alignItems: "center" }}>

                        <PersonIcon />

                        {job.label}
                      </div>
                    </TableCell>
                    <TableCell sx={{ textWrap: "nowrap" }} align="center">
                      {formatDate(job.createdAt)}
                    </TableCell>

                    <TableCell align="center">
                      {getStatusDisplay(
                        job.generation?.status,
                        job.generation?.error
                      )}
                      {job?.generation?.status !== "FAILED" &&
                        job?.generation?.opState?.savedLeads &&
                        job?.generation?.opState?.totalLeads && (
                          <Tooltip
                            title="Saved leads / Total leads"
                            placement="top"
                            arrow
                          >
                            <Badge
                              sx={{
                                backgroundColor: (theme) =>
                                  theme.palette.primary.main, // alpha("#5761FE", 0.2)
                                borderRadius: "30px",
                                py: "4px",
                                px: "8px",
                                color: (theme) =>
                                  theme.palette.primary.contrastText,
                                fontSize: "12px",
                                fontWeight: "500",
                                minWidth: "60px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <span>
                                {job?.generation?.opState?.savedLeads?.toLocaleString() ||
                                  0}{" "}
                                /{" "}
                                {job?.generation?.opState?.totalLeads?.toLocaleString() ||
                                  0}
                              </span>
                            </Badge>
                          </Tooltip>
                        )}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          if (!job.fileLink) return;
                          // Extract extension from URL
                          const urlExtension = job.fileLink.split(".").pop()?.split("?")[0] || "xlsx";
                          // Check if label already has an extension
                          const hasExtension = job.label && /\.(csv|xlsx|xls)$/i.test(job.label);
                          const filename = job.label 
                            ? (hasExtension ? job.label : `${job.label}.${urlExtension}`)
                            : `export.${urlExtension}`;
                          downloadBlobFile(job.fileLink, filename);
                        }}
                        disabled={
                          job?.generation?.status !== "COMPLETED" ||
                          !job.fileLink
                        }
                      >
                        Download
                      </Button>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        className="delete-icon"
                        size="small"
                        onClick={() => deletejob(job?._id)}
                        sx={{
                          transition: "all 0.2s",
                          padding: "4px",
                          "& svg": {
                            fontSize: "18px",
                          },
                        }}
                      >
                        <Delete sx={{ marginLeft: "5px", color: "red" }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>

        {/* Pagination Component */}
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={(_, value) => setCurrentPage(value)}
          sx={{
            display: "flex",
            justifyContent: "center",
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            py: 1,
          }}
        />
      </TableContainer>
    </Box>
  );
};

export default ExportLeadTable;
